import { lazy, Suspense } from 'react';

import { UNNAMED } from '@game/constants';
import { Notifications } from '@game/ui';
import type { JSX } from 'react';
import { Route, Switch, useLocation } from 'wouter';

import { Rocket } from '@components/Rocket/Rocket';
import { LOGOUT } from '@constants/paths';
import { AuthStatus } from '@store/authState';
import { authStore } from '@store/authStore';
import { persistentStore } from '@store/persistentStore';
import { store } from '@store/store';

import Public from './views/Public/Public';

import './App.less';

const Logout = lazy(() => import('./views/Logout/Logout'));
const InputName = lazy(() => import('./views/InputName/InputName'));
const Reconnecting = lazy(() => import('@components/Reconnecting/Reconnecting'));
const AuthenticatedView = lazy(() => import('./views/AuthenticatedView/AuthenticatedView'));

const App = (): JSX.Element => {
  const [location, setLocation] = useLocation();

  const name = store((s) => s.name);
  const error = authStore((s) => s.error);
  const status = authStore((s) => s.status);
  const anonymous = authStore((s) => s.anonymous);
  const expectSignIn = persistentStore((s) => s.expectSignIn);
  const reconnectAttempt = store((s) => s.reconnectAttempt);
  const mustChangeName = store((s) => s.mustChangeName);

  let content: JSX.Element;

  if ((status === AuthStatus.LOGGED_IN_CONNECTED_TO_GAME_SERVER && name === UNNAMED && !anonymous) || mustChangeName) {
    content = (
      <Suspense fallback={null}>
        <Switch>
          <Route path="*">
            <InputName />
          </Route>
        </Switch>
      </Suspense>
    );
  } else if (location === LOGOUT) {
    content = (
      <Suspense fallback={null}>
        <Switch>
          <Route path={LOGOUT}>
            <Logout />
          </Route>
        </Switch>
      </Suspense>
    );
  } else if (reconnectAttempt > 0) {
    content = (
      <Suspense fallback={null}>
        <Reconnecting reconnectAttempt={reconnectAttempt} />
      </Suspense>
    );
  } else if (status !== AuthStatus.LOGGED_IN_CONNECTED_TO_GAME_SERVER) {
    if (error) {
      content = <div className="full-center app-problem">Encountered a problem: {error.message}</div>;
    } else if (expectSignIn) {
      content = <Rocket />;
    } else {
      content = <Public />;
    }
  } else {
    content = (
      <Suspense fallback={<Rocket />}>
        <AuthenticatedView />
      </Suspense>
    );
  }

  return (
    <>
      <Notifications onNavigate={setLocation} />
      {content}
    </>
  );
};

export default App;
